import React from "react";
import { Download, X, Smartphone } from "lucide-react";
import { useInstallPrompt, wasDismissed, markDismissed, isIos, isStandalone } from "../lib/install";

/**
 * iter-126: rewritten to use the shared install controller in
 * `../lib/install`. Behavioural changes:
 *   - Dismissal is now PERMANENT (was 7-day TTL). The user can still
 *     start installation from the "Install app" item in the user
 *     dropdown at any time.
 *   - Uses `useInstallPrompt()` so this component and the dropdown
 *     item share the SAME captured `BeforeInstallPromptEvent`.
 */
export default function InstallPrompt() {
    const { deferred, installed, promptInstall } = useInstallPrompt();
    const [showIosHelp, setShowIosHelp] = React.useState(false);

    React.useEffect(() => {
        if (installed) return;
        if (wasDismissed()) return;
        if (!isIos()) return;
        // iOS never fires beforeinstallprompt — surface the manual
        // Share→Add-to-Home-Screen hint after a short delay.
        const t = setTimeout(() => setShowIosHelp(true), 3500);
        return () => clearTimeout(t);
    }, [installed]);

    if (installed) return null;
    if (wasDismissed()) return null;

    const show = !!deferred;
    if (!show && !showIosHelp) return null;

    const dismiss = () => {
        markDismissed();
        setShowIosHelp(false);
        // Force a re-render by nudging state — the module dismiss flag
        // is checked on next render via wasDismissed().
        window.dispatchEvent(new Event("planflow-install-dismissed"));
    };

    return (
        <div
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] w-[calc(100vw-2rem)] max-w-[440px] rounded-lg border border-[#2A3655] bg-[#131A2F]/98 backdrop-blur-md shadow-2xl p-4 flex items-start gap-3"
            role="dialog"
            aria-live="polite"
            data-testid="pwa-install-prompt"
        >
            <div className="shrink-0 w-10 h-10 rounded-md bg-[#0A1128] border border-[#2A3655] flex items-center justify-center">
                {show ? (
                    <Download className="w-5 h-5 text-[#00A3FF]" />
                ) : (
                    <Smartphone className="w-5 h-5 text-[#00A3FF]" />
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-white">
                    Install <span className="text-[#00A3FF]">Steeltooth</span>
                </div>
                {show ? (
                    <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
                        Add to your home screen for full-screen takeoff + offline-friendly launch. Updates install automatically — no re-install needed.
                    </p>
                ) : (
                    <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
                        Tap <span className="font-bold text-white">Share</span> → <span className="font-bold text-white">Add to Home Screen</span> in Safari.
                    </p>
                )}
                {show && (
                    <div className="mt-2.5 flex items-center gap-2">
                        <button
                            onClick={promptInstall}
                            data-testid="pwa-install-accept"
                            className="inline-flex items-center gap-1.5 bg-[#007AFF] hover:bg-[#3395FF] text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm transition-colors"
                        >
                            <Download className="w-3.5 h-3.5" />
                            Install
                        </button>
                        <button
                            onClick={dismiss}
                            data-testid="pwa-install-not-now"
                            className="text-xs text-slate-400 hover:text-white font-semibold px-2 py-1.5 transition-colors"
                        >
                            Not now
                        </button>
                    </div>
                )}
            </div>
            <button
                onClick={dismiss}
                aria-label="Dismiss install prompt"
                data-testid="pwa-install-dismiss"
                className="shrink-0 w-7 h-7 rounded-md text-slate-500 hover:text-white hover:bg-[#1C253C] flex items-center justify-center transition-colors"
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
}

// Re-export so any legacy caller keeps working.
export { isStandalone };
